import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { Draggable } from "react-beautiful-dnd";
import Tooltip from "@material-ui/core/Tooltip";
import { ThreeDots } from "react-loader-spinner";
import useThemeColors from "../../hooks/useThemeColors";

const useStyles = makeStyles(() => ({
  cardItem: {
    backgroundColor: "#fff",
    borderRadius: "3px",
    marginBottom: "-6px",
    padding: "4px 3px 4px 7px",
    cursor: "pointer",
    marginLeft: "8px",
    marginRight: "8px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "50px",
  },
  titleWrapper: {
    display: "block",
    cursor: "pointer",
    lineHeight: "14px",
    width: "100%",
    overflow: "hidden",
  },
  ellipsisText: {
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%",
    maxWidth: "100%",
    lineHeight: 1.4,
    minWidth: 0,
  },
  title: {
    color: "#555555",
    fontWeight: 400,
  },
  leaderLine: {
    color: "#999",
    fontSize: "12px",
  },
  leaderLabel: {
    fontWeight: 300,
    color: "#999",
  },
  leaderValue: {
    fontWeight: 400,
    color: "#555555",
  },
  benefitLine: {
    color: "#999",
    fontSize: "12px",
    lineHeight: 1.2,
  },
  benefitLabel: {
    fontWeight: 300,
    color: "#999",
  },
  benefitValue: {
    fontWeight: 400,
    color: "#555555",
  },
}));

const DraggableInitiativeCard = ({
  item,
  index,
  loadingInitiativeId,
  handleInitiativeClick,
  t,
  stageKey,
  selectedInitiativeId = null,
  hasSelection = false,
}) => {
  const classes = useStyles();
  const { clientColor } = useThemeColors();
  const isSelected = selectedInitiativeId === item.id;
  const isFaded = hasSelection && !isSelected;
  const statusToColor = (status) => {
    switch (status) {
      case "green":
      case "active":
        return "#4CAF50";
      case "yellow":
      case "on_hold":
        return "#FF9800";
      case "red":
      case "cancelled":
        return "#F44336";
      case "finished":
        return "#9E9E9E";
      default:
        return "#9E9E9E";
    }
  };

  return (
    <Draggable key={item.id} draggableId={String(item.id)} index={index}>
      {(dragProvided, snapshot) => (
        <div
          className={classes.cardItem}
          ref={dragProvided.innerRef}
          {...dragProvided.draggableProps}
          {...dragProvided.dragHandleProps}
          style={{
            opacity: snapshot.isDragging ? 0.8 : isFaded ? 0.4 : 1,
            transform: snapshot.isDragging
              ? `${dragProvided.draggableProps.style?.transform} rotate(5deg)`
              : dragProvided.draggableProps.style?.transform,
            borderTop: `2px solid ${statusToColor(
              stageKey === "opportunity" ? "" : item.displayStatus,
            )}`,
            transition: "opacity 0.2s ease",
            ...dragProvided.draggableProps.style,
          }}
        >
          {loadingInitiativeId === item.id ? (
            <div className={classes.loadingContainer}>
              <ThreeDots color={clientColor} height={20} width={20} />
            </div>
          ) : (
            <>
              <div
                className={classes.titleWrapper}
                onClick={() => handleInitiativeClick(item.id)}
              >
                <Tooltip title={item.initiative} arrow>
                  <span className={`${classes.ellipsisText} ${classes.title}`}>
                    {item.initiative}
                  </span>
                </Tooltip>
              </div>
              {/* Leader */}
              <div className={classes.leaderLine}>
                <strong className={classes.leaderLabel}>
                  {t("initiatives.leader")}:
                </strong>{" "}
                <span className={classes.leaderValue}>{item.leader}</span>
              </div>

              {/* Benefício Global */}
              <div className={classes.benefitLine}>
                <strong className={classes.benefitLabel}>
                  {t("initiatives.benefit_global")}:
                </strong>{" "}
                <span className={classes.benefitValue}>
                  {(item.margin_benefit_global * 100).toFixed(1)}%
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </Draggable>
  );
};

DraggableInitiativeCard.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  loadingInitiativeId: PropTypes.number,
  handleInitiativeClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  stageKey: PropTypes.string.isRequired,
  selectedInitiativeId: PropTypes.number,
  hasSelection: PropTypes.bool,
};

export default DraggableInitiativeCard;
